import { useCallback, useMemo, useState } from 'react';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import {
  SimuladoDraftStore,
  type SimuladoDraft,
} from '../../data/datasources/simulado-draft.store';
import {
  setAnswer,
  type AnswerOption,
  type Answers,
} from '../../domain/entities/answer.entity';

/**
 * O simulado desta prova no navegador: as respostas, se já foi enviado, e as
 * três operações sobre ele. Toda mudança é gravada na hora — é isso que faz o
 * reload no meio do ensaio não custar nada.
 */
export function useSimuladoDraft() {
  const { id, edition } = useTenant();
  const store = useMemo(
    () => new SimuladoDraftStore(id, edition.year),
    [id, edition.year],
  );
  const [draft, setDraft] = useState<SimuladoDraft>(() => store.read());

  const update = useCallback(
    (next: SimuladoDraft) => {
      store.save(next);
      setDraft(next);
    },
    [store],
  );

  /**
   * Confirma a resposta da questão — ou a deixa em branco com `null`. É o
   * único caminho para o rascunho: escolher uma alternativa no card não grava
   * nada até a confirmação.
   */
  const confirm = useCallback(
    (question: number, option: AnswerOption | null) => {
      update({ ...draft, answers: setAnswer(draft.answers, question, option) });
    },
    [draft, update],
  );

  /**
   * Troca todas as respostas de uma vez — a folha de "Fornecer respostas".
   * Substitui, e não soma: a folha abre com o que já estava marcado, então o
   * que a pessoa desmarcou nela também precisa sair.
   */
  const replaceAll = useCallback(
    (answers: Answers) => update({ ...draft, answers }),
    [draft, update],
  );

  return { draft, confirm, replaceAll };
}
